import React, { Fragment, useContext, useEffect, useMemo } from "react";
import { AppContext } from "../../context/AppContext";
import EnquiryListPanel from "./EnquiryListPanel";

function toDateKey(bookingTime) {
  if (!bookingTime) return null;
  const raw = String(bookingTime).trim();
  if (raw.includes(" ")) return raw.split(" ")[0];
  if (raw.includes("T")) return raw.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return null;
}

function matchesStatusFilter(item, filterId) {
  const status = item?.status;
  if (filterId === "all") return true;
  if (filterId === "needs_approval") {
    return status === "Requested" || status === "Modified";
  }
  if (filterId === "Approved" || filterId === "Confirmed") {
    return status === "Approved";
  }
  return status === filterId;
}

function mergeById(...lists) {
  const byId = new Map();
  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (item?.id == null) return;
      byId.set(String(item.id), item);
    });
  });
  return Array.from(byId.values());
}

function parseItem(item) {
  return item && typeof item === "string" ? JSON.parse(item) : item;
}

function TodaysEnquiries({
  keyword = "",
  selectedDate,
  statusFilter = "all",
}) {
  const {
    todayEnquiries,
    upcomingEnquiries,
    enquiriesHistory,
    recentEnquiries,
    tableLists,
    reservationLoading,
  } = useContext(AppContext);

  const sourceList = useMemo(() => {
    return mergeById(
      todayEnquiries,
      upcomingEnquiries,
      enquiriesHistory,
      recentEnquiries?.enquiries,
      recentEnquiries?.modified_enquiries
    );
  }, [todayEnquiries, upcomingEnquiries, enquiriesHistory, recentEnquiries]);

  const dateFilteredList = useMemo(() => {
    if (!selectedDate) return [];
    return sourceList.filter(
      (item) => toDateKey(item?.bookingTime) === selectedDate
    );
  }, [sourceList, selectedDate]);

  useEffect(() => {
    dateFilteredList.forEach((item) => {
      const tableData = item?.tables;
      if (!tableData) {
        item.tablesDetails = null;
        return;
      }

      let parsedTables = parseItem(tableData);
      if (parsedTables && typeof parsedTables === "string") {
        parsedTables = parseItem(parsedTables);
      }

      if (parsedTables && parsedTables.length != 0) {
        item.tablesDetails = Array.isArray(parsedTables)
          ? parsedTables.map((table) => {
              const tableInfo =
                tableLists && tableLists.find((t) => t.id == table);
              return tableInfo ? tableInfo : null;
            })
          : null;
      } else {
        item.tablesDetails = null;
      }
    });
  }, [dateFilteredList, tableLists]);

  const filteredData = useMemo(() => {
    if (!dateFilteredList || dateFilteredList.length === 0) return [];

    const byStatus = dateFilteredList.filter((item) =>
      matchesStatusFilter(item, statusFilter)
    );

    const kw = (keyword || "").toLowerCase();
    if (!kw) return byStatus;

    return byStatus.filter(
      (item) =>
        item.name?.toLowerCase?.().includes(kw) ||
        item.email?.toLowerCase?.().includes(kw) ||
        item.formattedID?.toLowerCase?.().includes(kw) ||
        String(item.phone || "").includes(keyword)
    );
  }, [dateFilteredList, keyword, statusFilter]);

  const emptyMessage =
    statusFilter === "all"
      ? "No reservations for this date"
      : statusFilter === "needs_approval"
        ? "No reservations need approval on this date"
        : `No ${statusFilter === "Approved" ? "Confirmed" : statusFilter} reservations on this date`;

  return (
    <Fragment>
      <EnquiryListPanel
        title="Your guest list"
        items={filteredData}
        loading={reservationLoading}
        emptyMessage={emptyMessage}
        showStatus
      />
    </Fragment>
  );
}

export default TodaysEnquiries;

export { toDateKey, mergeById, matchesStatusFilter };
